import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { OpenShiftCardContainer } from "@src/appV2/redesign/Shift/Open/CardContainer";
import type { Offer, OpenShift, OpenShiftWorkplace } from "@src/appV2/redesign/Shift/Open/types";
import { Title } from "@src/appV2/redesign/system/ui-components";

interface NearestOpenShiftsListProps {
  readonly shifts: OpenShift[];
  readonly workplacesMap: Map<string, OpenShiftWorkplace>;
  readonly onClickOpenShift: (params: {
    shift: OpenShift;
    workplace: OpenShiftWorkplace;
    offer?: Offer;
  }) => void;
  readonly maxShiftsToDisplay?: number;
}

export const MAX_NEAREST_SHIFTS_TO_DISPLAY = 5;

/**
 * Displays nearest available shifts found via progressive distance expansion.
 */
export function NearestOpenShiftsList(props: NearestOpenShiftsListProps) {
  const {
    shifts,
    workplacesMap,
    onClickOpenShift,
    maxShiftsToDisplay = MAX_NEAREST_SHIFTS_TO_DISPLAY,
  } = props;

  if (shifts.length === 0) {
    return null;
  }

  return (
    <Stack spacing={5} sx={{ py: 5 }}>
      <Stack spacing={1}>
        <Title semibold variant="h5" component="h3">
          Nearest available shifts
        </Title>
      </Stack>

      {shifts.slice(0, maxShiftsToDisplay).map((shift) => {
        const workplace = workplacesMap.get(shift.relationships.workplace.data.id);
        if (!isDefined(workplace)) {
          return null;
        }

        return (
          <OpenShiftCardContainer
            key={shift.id}
            isFromNearestOpenShifts
            shift={shift}
            workplace={workplace}
            blockingRequirementsContext={undefined}
            workers={[]}
            friendsMap={new Map()}
            recentColleaguesMap={new Map()}
            onClick={({ offer }) => {
              onClickOpenShift({ shift, workplace, offer });
            }}
          />
        );
      })}
    </Stack>
  );
}
